import React, { useEffect, useState } from 'react';
import axios from 'axios';
import './PublicationsSettings.css';

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

const emptySection = () => ({ heading: '', image: '', paragraphs: [] });
const emptyPublication = () => ({
  id: '',
  title: '',
  category: '',
  date: '',
  summary: '',
  heroImage: '',
  sections: [emptySection()],
});

function PublicationsSettings() {
  const [publications, setPublications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [openIndex, setOpenIndex] = useState(0);

  useEffect(() => {
    const fetchList = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/publications`);
        setPublications(Array.isArray(res.data.publications) ? res.data.publications : []);
      } catch (err) {
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchList();
  }, []);

  const setPub = (index, field, value) => {
    setPublications((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const setSection = (pubIndex, secIndex, field, value) => {
    setPublications((prev) => {
      const next = [...prev];
      const secs = [...(next[pubIndex].sections || [])];
      secs[secIndex] = { ...secs[secIndex], [field]: value };
      next[pubIndex] = { ...next[pubIndex], sections: secs };
      return next;
    });
  };

  const setSectionParagraphs = (pubIndex, secIndex, textareaValue) => {
    const paragraphs = textareaValue
      .split('\n')
      .map((p) => p.trim())
      .filter(Boolean);
    setSection(pubIndex, secIndex, 'paragraphs', paragraphs);
  };

  const getSectionParagraphsText = (pubIndex, secIndex) => {
    const sec = publications[pubIndex] && publications[pubIndex].sections && publications[pubIndex].sections[secIndex];
    return sec && Array.isArray(sec.paragraphs) ? sec.paragraphs.join('\n') : '';
  };

  const handleAddPublication = () => {
    setPublications((prev) => [...prev, emptyPublication()]);
    setOpenIndex(publications.length);
  };

  const handleDeletePublication = (index) => {
    setPublications((prev) => prev.filter((_, i) => i !== index));
    setOpenIndex((prev) => (prev === index ? 0 : prev > index ? prev - 1 : prev));
  };

  const handleAddSection = (pubIndex) => {
    setPublications((prev) => {
      const next = [...prev];
      const pub = next[pubIndex];
      const sections = [...(pub.sections || []), emptySection()];
      next[pubIndex] = { ...pub, sections };
      return next;
    });
  };

  const handleDeleteSection = (pubIndex, secIndex) => {
    setPublications((prev) => {
      const next = [...prev];
      const pub = next[pubIndex];
      const sections = (pub.sections || []).filter((_, i) => i !== secIndex);
      next[pubIndex] = { ...pub, sections: sections.length ? sections : [emptySection()] };
      return next;
    });
  };

  const handleImageUpload = async (setter) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = e.target && e.target.files && e.target.files[0];
      if (!file) return;
      const formData = new FormData();
      formData.append('image', file);
      try {
        const res = await axios.post(
          `${API_BASE_URL}/api/publications/upload`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        if (res.data && res.data.url) setter(res.data.url);
      } catch (err) {
        console.error(err);
        setMessage('Upload failed.');
      }
    };
    input.click();
  };

  const handleSave = async () => {
    setIsSaving(true);
    setMessage('');
    try {
      const res = await axios.put(`${API_BASE_URL}/api/publications`, {
        publications,
      });
      if (Array.isArray(res.data.publications)) {
        setPublications(res.data.publications);
      }
      setMessage('Publications saved successfully.');
    } catch (err) {
      console.error(err);
      setMessage('Unable to save. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <p className="publications-settings-loading">Loading publications…</p>
    );
  }

  return (
    <div className="publications-settings">
      <div className="publications-settings-header">
        <div>
          <h2 className="publications-settings-title">Publications</h2>
          <p className="publications-settings-subtitle">
            Add, edit, or remove publications. Each publication has a detail page at /publications/:id.
          </p>
        </div>
        <div className="publications-settings-actions">
          <button
            type="button"
            className="publications-settings-secondary-btn"
            onClick={handleAddPublication}
          >
            + Add publication
          </button>
          <button
            type="button"
            className="publications-settings-primary-btn"
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>

      <div className="publications-settings-list">
        {publications.map((pub, pIdx) => (
          <div key={pub.id || pIdx} className="publications-settings-pub">
            <button
              type="button"
              className="publications-settings-pub-toggle"
              onClick={() => setOpenIndex(openIndex === pIdx ? -1 : pIdx)}
            >
              {openIndex === pIdx ? '▼' : '▶'} {pub.title || 'Publication ' + (pIdx + 1)}
            </button>
            {openIndex === pIdx && (
              <div className="publications-settings-pub-body">
                <div className="publications-settings-actions-row">
                  <button
                    type="button"
                    className="publications-settings-danger-btn"
                    onClick={() => handleDeletePublication(pIdx)}
                  >
                    Delete publication
                  </button>
                </div>
                <label className="publications-settings-label">
                  ID (number or slug for URL)
                  <input
                    type="text"
                    className="publications-settings-input"
                    value={pub.id ?? ''}
                    onChange={(e) => setPub(pIdx, 'id', e.target.value)}
                    placeholder="1"
                  />
                </label>
                <label className="publications-settings-label">
                  Title
                  <input
                    type="text"
                    className="publications-settings-input"
                    value={pub.title || ''}
                    onChange={(e) => setPub(pIdx, 'title', e.target.value)}
                  />
                </label>
                <label className="publications-settings-label">
                  Category
                  <input
                    type="text"
                    className="publications-settings-input"
                    value={pub.category || ''}
                    onChange={(e) => setPub(pIdx, 'category', e.target.value)}
                    placeholder="CAPITAL MARKETS"
                  />
                </label>
                <label className="publications-settings-label">
                  Date
                  <input
                    type="text"
                    className="publications-settings-input"
                    value={pub.date || ''}
                    onChange={(e) => setPub(pIdx, 'date', e.target.value)}
                    placeholder="15 Jan 2026"
                  />
                </label>
                <label className="publications-settings-label">
                  Summary
                  <textarea
                    className="publications-settings-textarea"
                    rows={2}
                    value={pub.summary || ''}
                    onChange={(e) => setPub(pIdx, 'summary', e.target.value)}
                  />
                </label>
                <label className="publications-settings-label">
                  Hero image URL
                  <input
                    type="text"
                    className="publications-settings-input"
                    value={pub.heroImage || ''}
                    onChange={(e) => setPub(pIdx, 'heroImage', e.target.value)}
                  />
                </label>
                <button
                  type="button"
                  className="publications-settings-secondary-btn"
                  onClick={() =>
                    handleImageUpload((url) => setPub(pIdx, 'heroImage', url))
                  }
                >
                  Upload hero image
                </button>

                <h4 className="publications-settings-subtitle">Sections</h4>
                {(pub.sections || []).map((sec, sIdx) => (
                  <div
                    key={sIdx}
                    className="publications-settings-section"
                  >
                    <div className="publications-settings-actions-row">
                      <button
                        type="button"
                        className="publications-settings-danger-btn"
                        onClick={() => handleDeleteSection(pIdx, sIdx)}
                      >
                        Remove section
                      </button>
                    </div>
                    <label className="publications-settings-label">
                      Section heading
                      <input
                        type="text"
                        className="publications-settings-input"
                        value={sec.heading || ''}
                        onChange={(e) =>
                          setSection(pIdx, sIdx, 'heading', e.target.value)
                        }
                      />
                    </label>
                    <label className="publications-settings-label">
                      Section image URL
                      <input
                        type="text"
                        className="publications-settings-input"
                        value={sec.image || ''}
                        onChange={(e) =>
                          setSection(pIdx, sIdx, 'image', e.target.value)
                        }
                      />
                    </label>
                    <button
                      type="button"
                      className="publications-settings-secondary-btn"
                      onClick={() =>
                        handleImageUpload((url) =>
                          setSection(pIdx, sIdx, 'image', url)
                        )
                      }
                    >
                      Upload section image
                    </button>
                    <label className="publications-settings-label">
                      Paragraphs (one per line)
                      <textarea
                        className="publications-settings-textarea"
                        rows={5}
                        value={getSectionParagraphsText(pIdx, sIdx)}
                        onChange={(e) =>
                          setSectionParagraphs(pIdx, sIdx, e.target.value)
                        }
                      />
                    </label>
                  </div>
                ))}
                <button
                  type="button"
                  className="publications-settings-secondary-btn"
                  onClick={() => handleAddSection(pIdx)}
                >
                  + Add section
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {publications.length === 0 && (
        <p className="publications-settings-empty">
          No publications yet. Click &quot;Add publication&quot; to create one.
        </p>
      )}

      {message && (
        <p className="publications-settings-message">{message}</p>
      )}
    </div>
  );
}

export default PublicationsSettings;
